"use client";

import { ChevronRight, Mail, MapPin, Phone } from "lucide-react";
import React from "react";
import useOpenMaps from "@/components/hooks/use-open-maps";
import Flex from "@/components/layout/Flex";
import Logo from "@/components/media/Logo";
import Typography from "@/components/shared/Typography";
import Button from "@/components/ui/Button";
import Drawer from "@/components/ui/Drawer";
import { getServiceCategories, getServiceCategory } from "@/content/helpers";
import {
  ADDRESS_CITY_STATE_ZIP,
  ADDRESS_STREET,
  HREF_EMAIL,
  HREF_PHONE_NUMBER,
} from "@/lib/consts";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import type { MobileMenuProps } from "../types";
import IndustriesSlide from "./industries-slide";
import RootSlide from "./root-slide";
import ServicesSlide, { SERVICES_SLIDE_INDEX } from "./services-slide";
import Slide from "./slide";

const SERVICE_CATEGORIES = getServiceCategories();

function MobileMenu({ className, ...props }: MobileMenuProps) {
  const [activeSlide, setActiveSlide] = React.useState(0);
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);
  const openMaps = useOpenMaps();

  const handleGoToSlide = React.useCallback((newIndex: number) => {
    setActiveSlide(newIndex);
  }, []);

  return (
    <Drawer.Content
      className={cn("w-full rounded-sm bg-popover", className, classes)}
      {...native}
    >
      <Flex.Column fill justify="start" items="center" className="relative">
        <Drawer.Header className="flex flex-row justify-between items-center w-full ~gap-4/8">
          <Drawer.Title>
            <Logo />
          </Drawer.Title>

          <Drawer.Close asChild>
            <Button.Icon
              icon="x"
              variant="outline"
              className="border-foreground"
              aria-label="Close Navigation Menu"
            />
          </Drawer.Close>
        </Drawer.Header>

        <Flex.Column fill justify="start" items="center" className="relative">
          <RootSlide activeSlide={activeSlide} goToSlide={handleGoToSlide} />

          <ServicesSlide
            activeSlide={activeSlide}
            goToSlide={handleGoToSlide}
          />

          {SERVICE_CATEGORIES.map((category, index) => (
            <Slide
              key={category.label}
              index={SERVICES_SLIDE_INDEX + index + 1}
              activeSlide={activeSlide}
            >
              <Flex.Row
                className="relative w-full"
                justify="between"
                items="center"
              >
                <Button.Icon
                  className="absolute left-0 border-foreground"
                  icon="arrow-left"
                  variant="outline"
                  onClick={() => handleGoToSlide(SERVICES_SLIDE_INDEX)}
                  aria-label="Go Back"
                />

                <Typography.H2 className="w-full flex flex-1 justify-center items-center">
                  {category.label}
                </Typography.H2>
              </Flex.Row>

              {getServiceCategory(category.key).items.map(item => (
                <Drawer.Close key={item.label} asChild>
                  <Button.Link
                    href={item.href}
                    variant="ghost"
                    className="w-full h-auto flex flex-row justify-between items-center rounded-none border-foreground border-b-2"
                    aria-label={`Go to the ${item.label} Page`}
                  >
                    <Flex.Column justify="start" items="start">
                      <Typography.Small className="text-left text-sm leading-none font-medium w-full">
                        {item.label}
                      </Typography.Small>

                      <Typography.Small className="text-left text-muted-foreground line-clamp-2 text-sm leading-snug w-full">
                        {item.description}
                      </Typography.Small>
                    </Flex.Column>
                    <ChevronRight />
                  </Button.Link>
                </Drawer.Close>
              ))}
            </Slide>
          ))}

          <IndustriesSlide
            activeSlide={activeSlide}
            goToSlide={handleGoToSlide}
          />
        </Flex.Column>

        <Flex.Column className="m-4">
          <Button.Link
            className="pl-0"
            variant="link"
            href={HREF_PHONE_NUMBER}
            aria-label="Click to call us"
          >
            <Phone />
            (713) 621-5000
          </Button.Link>

          <Button.Link
            className="pl-0"
            variant="link"
            href={HREF_EMAIL}
            aria-label="Click to email us"
          >
            <Mail />
            info@orsakcpas.com
          </Button.Link>

          <Flex.Row>
            <Button
              variant="link"
              onClick={openMaps}
              className="pl-0 gap-2 w-full h-full"
              aria-label="Click to open maps"
            >
              <MapPin />

              <Flex.Column fill gap="gap-" justify="center" items="start">
                <Typography.Small>
                  Orsak Langner & Barthel PLLC
                </Typography.Small>
                <Typography.Small>{ADDRESS_STREET}</Typography.Small>
                <Typography.Small>{ADDRESS_CITY_STATE_ZIP}</Typography.Small>
              </Flex.Column>
            </Button>
          </Flex.Row>
        </Flex.Column>
      </Flex.Column>
    </Drawer.Content>
  );
}

export default MobileMenu;
